import { Button, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import adminApi from "../../../../api/adminApi";
import type { DemoRefreshOperation } from "../../../../api/types/demoRefreshTypes";
import useClientContext from "../../../../context/useClientContext";
import DemoRefreshConfirmationDialog from "./DemoRefreshConfirmationDialog";
import DemoRefreshStatusLine from "./DemoRefreshStatusLine";
import { isDemoRefreshRunning } from "./demoRefreshHelper";

interface Props {
  templateClientCode: string;
}

const RefreshDemoCompanyButton = ({ templateClientCode }: Props) => {
  const { clientCode } = useClientContext();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const getLatestDemoRefresh = useCallback(
    (signal?: AbortSignal) => adminApi.getLatestDemoRefresh(clientCode, signal),
    [clientCode]
  );

  // A company that was never refreshed answers 404, so a failed read means "no refresh yet" and is ignored.
  const [latestOperation, , { setData, isFetching, fetch }] = useFetch(getLatestDemoRefresh);

  const handleStarted = (operation: DemoRefreshOperation) => {
    setConfirmationOpen(false);
    setData(operation);
  };

  const refreshIsRunning = isDemoRefreshRunning(latestOperation);
  useSetInterval(fetch, 15_000, { disabled: !refreshIsRunning });

  const disabledReason = refreshIsRunning
    ? "A refresh of this demo company is already running."
    : isFetching
      ? "Checking for a running refresh…"
      : undefined;

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <DemoRefreshStatusLine operation={latestOperation} />
        <TooltipForDisabledAction title={disabledReason} isActionDisabled={disabledReason !== undefined}>
          <Button variant="contained" disabled={disabledReason !== undefined} onClick={() => setConfirmationOpen(true)}>
            Refresh Demo Company
          </Button>
        </TooltipForDisabledAction>
      </Stack>

      {confirmationOpen && (
        <DemoRefreshConfirmationDialog
          templateClientCode={templateClientCode}
          onClose={() => setConfirmationOpen(false)}
          onStarted={handleStarted}
        />
      )}
    </>
  );
};

export default RefreshDemoCompanyButton;
